import React, { useEffect, useMemo, useRef, useState } from 'react';
import { uploadFile, buildKey, abortUpload, isUploadConfigured, collectUploaderMeta } from '../data/uploadService';
import { recordAsset, isRawName, isRawVideoName, useTripAssets, isTrashed } from '../data/firestore';
import { sanitizeSeg, sanitizePath, rdcSegmentOf, innerOfRdc, byName, readEntry } from '../utils/rdc';
import { createMonitor, makeSpeedSampler } from '../utils/transferMonitor';
import { runSpeedTest, connectionInfo } from '../utils/netProbe';
import UploadStats from './UploadStats';

const fmtBytes = (n) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / 1024 ** i).toFixed(1)} ${u[i]}`;
};
const fmtEta = (s) => {
  if (!Number.isFinite(s) || s <= 0) return '';
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};
const kindOf = (file) =>
  (file.type || '').startsWith('image/') || isRawName(file.name) ? 'photo' : 'video';

let uid = 0;

// Recursively read a File System Access API directory handle into {file,relPath}
// entries (relPath starts with the chosen folder's name, like webkitRelativePath).
async function readDirHandle(dir, prefix, out) {
  // eslint-disable-next-line no-restricted-syntax
  for await (const entry of dir.values()) {
    if (entry.kind === 'file') {
      // eslint-disable-next-line no-await-in-loop
      const f = await entry.getFile();
      out.push({ file: f, relPath: `${prefix}${entry.name}` });
    } else if (entry.kind === 'directory') {
      // eslint-disable-next-line no-await-in-loop
      await readDirHandle(entry, `${prefix}${entry.name}/`, out);
    }
  }
}

export default function Uploader({ trip }) {
  const inputRef = useRef(null);
  const folderRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [notice, setNotice] = useState(null); // guidance banner (e.g. orphan .R3D parts)
  const [items, setItems] = useState([]); // {id,name,size,kind,progress,loaded,status,error,speed,eta,reconnecting}
  const [online, setOnline] = useState(typeof navigator === 'undefined' || navigator.onLine !== false);
  const [conn, setConn] = useState(() => connectionInfo());
  const [testing, setTesting] = useState(false);
  const [snap, setSnap] = useState(null);
  const controllers = useRef({}); // id -> AbortController
  const monitor = useRef(createMonitor());
  const uploaderMetaRef = useRef(null); // captured once per batch
  const configured = isUploadConfigured();

  // Existing (non-trashed) trip assets, signed by name+byteSize, so we can skip
  // re-uploading a clip already in this trip. Kept in a ref so the async enqueue
  // handler always sees the latest set without being a dependency.
  const { docs: existingAssets } = useTripAssets(trip.id);
  const assetSigRef = useRef(new Set());
  useEffect(() => {
    const sigs = new Set();
    (existingAssets || []).forEach((a) => {
      if (!isTrashed(a) && a.name) sigs.add(`${a.name}\u0000${a.sizeBytes || 0}`);
    });
    assetSigRef.current = sigs;
  }, [existingAssets]);
  const isDuplicate = (name, size) => assetSigRef.current.has(`${name}\u0000${size || 0}`);

  const update = (id, patch) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const overall = useMemo(() => {
    const active = items.filter((it) => ['queued', 'uploading', 'done'].includes(it.status));
    const total = active.reduce((s, it) => s + it.size, 0);
    const loaded = active.reduce((s, it) => s + (it.loaded || 0), 0);
    const uploading = items.filter((it) => it.status === 'uploading');
    const speed = uploading.reduce((s, it) => s + (it.speed || 0), 0);
    const remaining = total - loaded;
    return {
      total,
      loaded,
      pct: total ? loaded / total : 0,
      speed,
      eta: speed > 0 ? remaining / speed : 0,
      activeCount: uploading.length,
      reconnectingCount: uploading.filter((it) => it.reconnecting).length,
      doneCount: items.filter((it) => it.status === 'done').length,
      n: active.length,
    };
  }, [items]);

  const anyActive = overall.n > 0 && overall.doneCount < overall.n;

  // Put the folder input into directory-select mode. Setting these as JSX
  // attributes isn't reliable across React versions (the input falls back to a
  // normal file picker that makes you choose files inside the folder), so we set
  // them directly on the DOM node.
  useEffect(() => {
    const el = folderRef.current;
    if (!el) return;
    el.setAttribute('webkitdirectory', '');
    el.setAttribute('directory', '');
    el.setAttribute('mozdirectory', '');
  }, []);

  // Connectivity banner.
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  // Sample telemetry + throughput while uploads run. Depends only on `anyActive`
  // (not overall.loaded) so the timer keeps a steady cadence instead of being
  // reset on every progress event; throughput comes from the monitor's wire-byte
  // counter so resumed/skipped bytes can't spike it.
  useEffect(() => {
    if (!anyActive) return undefined;
    const tick = () => {
      monitor.current.sampleThroughput();
      setSnap(monitor.current.snapshot());
    };
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [anyActive]);

  // Warn before navigating away mid-upload.
  useEffect(() => {
    if (!anyActive) return undefined;
    const h = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [anyActive]);

  const runTest = async () => {
    setTesting(true);
    try {
      setConn(await runSpeedTest());
    } finally {
      setTesting(false);
    }
  };

  const uploadOne = async (id, file, presetKey) => {
    // A folder-derived file carries a structure-preserving key; loose files get
    // a flat, timestamped key at the trip root.
    const key = presetKey || buildKey(trip.s3Prefix, file.name);
    const controller = new AbortController();
    controllers.current[id] = controller;
    const speedOf = makeSpeedSampler();
    try {
      update(id, { status: 'uploading', error: null, reconnecting: false });
      const { key: usedKey } = await uploadFile(file, {
        key,
        contentType: file.type,
        signal: controller.signal,
        monitor: monitor.current,
        onState: (st) => update(id, { reconnecting: !!st.reconnecting }),
        onRecognized: (r) => update(id, { resumedFrom: r }),
        onProgress: (p) => {
          const loaded = p * file.size;
          const speed = speedOf(loaded);
          update(id, { progress: p, loaded, speed, eta: speed > 0 ? (file.size - loaded) / speed : 0 });
        },
      });
      await recordAsset(trip.id, {
        name: file.name,
        sizeBytes: file.size,
        contentType: file.type,
        kind: kindOf(file),
        s3Key: usedKey || key,
        status: 'stored',
        uploaderMeta: uploaderMetaRef.current,
      });
      update(id, { status: 'done', progress: 1, loaded: file.size, speed: 0, eta: 0, reconnecting: false });
    } catch (err) {
      if (controller.signal.aborted) {
        update(id, { status: 'canceled', error: null, reconnecting: false });
        abortUpload(file, { bucket: 'raw' }).catch(() => {});
      } else {
        update(id, { status: 'error', error: err.message, reconnecting: false });
      }
    } finally {
      delete controllers.current[id];
    }
  };

  // Upload a whole .RDC folder: every part file is sent (structure preserved
  // in S3), but it's tracked as ONE asset pointing at the primary .R3D.
  const uploadRdcGroup = async (id, group) => {
    const controller = new AbortController();
    controllers.current[id] = controller;
    const total = group.totalSize || 1;
    const speedOf = makeSpeedSampler();
    let base = 0;
    try {
      update(id, { status: 'uploading', error: null, reconnecting: false });
      for (const part of group.files) {
        // eslint-disable-next-line no-await-in-loop
        await uploadFile(part.file, {
          key: part.key,
          contentType: part.file.type || 'application/octet-stream',
          signal: controller.signal,
          monitor: monitor.current,
          onState: (st) => update(id, { reconnecting: !!st.reconnecting }),
          onProgress: (p) => {
            const loaded = base + p * part.file.size;
            const speed = speedOf(loaded);
            update(id, { progress: loaded / total, loaded, speed, eta: speed > 0 ? (total - loaded) / speed : 0 });
          },
        });
        base += part.file.size;
      }
      await recordAsset(trip.id, {
        name: group.folderName,
        sizeBytes: total,
        contentType: 'video/x-red-r3d',
        kind: 'video',
        isRawVideo: true,
        rdc: true,
        rdcFormat: 'r3d',
        s3Key: group.primaryKey,
        folderPrefix: group.prefix,
        parts: group.files.map((p) => ({ name: p.file.name, s3Key: p.key, sizeBytes: p.file.size })),
        status: 'stored',
        uploaderMeta: uploaderMetaRef.current,
      });
      update(id, { status: 'done', progress: 1, loaded: total, speed: 0, eta: 0, reconnecting: false });
    } catch (err) {
      if (controller.signal.aborted) update(id, { status: 'canceled', error: null, reconnecting: false });
      else update(id, { status: 'error', error: err.message, reconnecting: false });
    } finally {
      delete controllers.current[id];
    }
  };

  const runItem = (it) => (it.group ? uploadRdcGroup(it.id, it.group) : uploadOne(it.id, it.file, it.key));

  // Split incoming {file, relPath} entries into .RDC folder groups + loose
  // files, then upload sequentially (huge files shouldn't fight for bandwidth).
  // Any file that arrived inside a folder keeps its relative path in the S3 key
  // so the folder structure is mirrored in storage (no flattening / comingling).
  const handleEntries = async (entries) => {
    const groupsByPrefix = new Map();
    const loose = []; // { file, relPath }
    let gi = 0;
    const batchTs = Date.now();
    // One unique S3 prefix per top-level folder in this batch, so a whole folder
    // lands together under trips/<id>/<ts>-<folder>/… with its tree intact.
    const folderPrefixes = new Map();
    const folderKeyFor = (relPath) => {
      const parts = relPath.split('/').filter(Boolean);
      if (parts.length < 2) return null; // not inside a folder
      const top = parts[0];
      if (!folderPrefixes.has(top)) {
        folderPrefixes.set(top, `${trip.s3Prefix}${batchTs}-${gi++}-${sanitizeSeg(top)}/`);
      }
      return `${folderPrefixes.get(top)}${sanitizePath(parts.slice(1).join('/'))}`;
    };

    for (const { file, relPath } of entries) {
      const seg = rdcSegmentOf(relPath);
      if (seg) {
        const groupId = `${seg}::${relPath.slice(0, relPath.toLowerCase().indexOf(seg.toLowerCase()))}`;
        let g = groupsByPrefix.get(groupId);
        if (!g) {
          const prefix = `${trip.s3Prefix}rdc/${batchTs}-${gi}-${sanitizeSeg(seg)}/`;
          gi += 1;
          g = { folderName: seg, prefix, files: [], totalSize: 0 };
          groupsByPrefix.set(groupId, g);
        }
        const inner = innerOfRdc(relPath, seg) || file.name;
        g.files.push({ file, key: `${g.prefix}${sanitizePath(inner)}` });
        g.totalSize += file.size;
      } else {
        loose.push({ file, relPath: relPath || file.name });
      }
    }

    // Finalize each group: pick the primary .R3D (first spanned part).
    const groups = [];
    for (const g of groupsByPrefix.values()) {
      g.files.sort((a, b) => byName(a.file, b.file));
      const r3ds = g.files.filter((p) => /\.r3d$/i.test(p.file.name));
      g.primaryKey = (r3ds[0] || g.files[0]).key;
      groups.push(g);
    }

    // Heads-up if RED .R3D parts were added individually (no .RDC parent) — they
    // can't be stitched back into one clip and will become broken assets. The
    // fix is to add the whole .RDC folder (drag it in / use "Choose a folder").
    const orphanR3d = loose.filter(({ file, relPath }) => isRawVideoName(file.name) && !rdcSegmentOf(relPath));

    let dupCount = 0;
    const groupItems = groups.map((g) => {
      // Same .RDC folder name + total byte size already in the trip → skip it so
      // we don't create a second copy (and a second decode job) of one clip.
      const dup = configured && isDuplicate(g.folderName, g.totalSize);
      if (dup) dupCount += 1;
      return {
        id: `u${uid++}`,
        group: g,
        name: g.folderName,
        size: g.totalSize,
        kind: 'rdc',
        progress: 0,
        loaded: 0,
        speed: 0,
        eta: 0,
        dup,
        status: !configured ? 'blocked' : dup ? 'blocked' : 'queued',
        error: !configured
          ? 'Upload backend not configured yet'
          : dup
          ? 'Already in this trip — skipped to avoid a duplicate'
          : null,
      };
    });
    const looseItems = loose.map(({ file, relPath }) => {
      const inFolder = relPath.includes('/');
      // A lone .R3D part (no .RDC parent) can't stand on its own — block it and
      // point the user at folder upload instead of uploading a broken asset.
      const orphan = isRawVideoName(file.name) && !rdcSegmentOf(relPath);
      const dup = configured && !orphan && isDuplicate(file.name, file.size);
      if (dup) dupCount += 1;
      return {
        id: `u${uid++}`,
        file,
        key: inFolder ? folderKeyFor(relPath) : undefined,
        folder: inFolder ? relPath.split('/')[0] : '',
        name: inFolder ? relPath : file.name,
        size: file.size,
        kind: isRawVideoName(file.name) ? 'rdc' : kindOf(file),
        progress: 0,
        loaded: 0,
        speed: 0,
        eta: 0,
        dup,
        status: !configured ? 'blocked' : orphan || dup ? 'blocked' : 'queued',
        error: !configured
          ? 'Upload backend not configured yet'
          : orphan
          ? 'Add the whole .RDC folder so its parts stay together'
          : dup
          ? 'Already in this trip — skipped to avoid a duplicate'
          : null,
      };
    });

    const notes = [];
    if (orphanR3d.length) {
      notes.push(
        `${orphanR3d.length} RED .R3D part${orphanR3d.length > 1 ? 's were' : ' was'} added on their own. ` +
          'RED clips are folders of spanned parts — add the whole .RDC folder (drag it in, or use “Choose a folder”) so its parts stay together as one clip.'
      );
    }
    if (dupCount) {
      notes.push(
        `${dupCount} item${dupCount > 1 ? 's were' : ' was'} already in this trip ` +
          '(same name + size) and skipped to avoid duplicates. Delete the existing copy first if you mean to replace it.'
      );
    }
    setNotice(notes.length ? { kind: 'warn', text: notes.join(' ') } : null);

    const all = [...groupItems, ...looseItems];
    setItems((prev) => [...all, ...prev]);
    if (!configured) return;
    // Capture uploader provenance once for this batch (server IP/geo + this
    // device's fingerprint + optional GPS). Best-effort; never blocks uploads.
    try {
      uploaderMetaRef.current = await collectUploaderMeta();
    } catch {
      uploaderMetaRef.current = null;
    }
    for (const it of all) {
      if (it.status !== 'queued') continue; // skip blocked (e.g. orphan .R3D parts)
      // eslint-disable-next-line no-await-in-loop
      await runItem(it);
    }
  };

  const handleFiles = (fileList) =>
    handleEntries(Array.from(fileList).map((f) => ({ file: f, relPath: f.webkitRelativePath || f.name })));

  // Pick a whole folder. Prefer the File System Access API (Chromium): a clean
  // "select this folder" dialog with no file-choosing. Everywhere else, fall
  // back to the hidden directory <input> (webkitdirectory).
  const pickFolder = async () => {
    if (typeof window !== 'undefined' && window.showDirectoryPicker) {
      try {
        const dir = await window.showDirectoryPicker({ mode: 'read' });
        const out = [];
        await readDirHandle(dir, `${dir.name}/`, out);
        if (out.length) await handleEntries(out);
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return; // user cancelled
        // Unsupported / blocked → fall through to the input.
      }
    }
    folderRef.current?.click();
  };

  const cancel = (id) => controllers.current[id]?.abort();
  const retry = (it) => runItem(it);
  const clearDone = () =>
    setItems((prev) => prev.filter((it) => !['done', 'canceled', 'error'].includes(it.status)));

  const onDrop = async (e) => {
    e.preventDefault();
    setDrag(false);
    const dt = e.dataTransfer;
    // Grab entry handles synchronously (the items list is consumed after the
    // event), then walk any dropped folders to preserve .RDC structure.
    const entryHandles = dt.items
      ? Array.from(dt.items).map((it) => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null)).filter(Boolean)
      : [];
    if (entryHandles.length) {
      const out = [];
      for (const en of entryHandles) {
        // eslint-disable-next-line no-await-in-loop
        await readEntry(en, '', out);
      }
      if (out.length) {
        handleEntries(out);
        return;
      }
    }
    if (dt.files?.length) handleFiles(dt.files);
  };

  return (
    <div>
      {!configured && (
        <div className="cs-empty" style={{ marginBottom: 16, padding: 18 }}>
          Footage uploads land in <code>{trip.s3Bucket}/{trip.s3Prefix}</code>. The upload backend
          isn’t wired up yet — files you pick will be listed but not sent.
        </div>
      )}

      <div
        className={`cs-dropzone ${drag ? 'drag' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
      >
        <div style={{ fontSize: 32 }}>⬆</div>
        <div style={{ marginTop: 8, fontWeight: 600, color: 'var(--text)' }}>
          Drop footage, photos, or whole folders here
        </div>
        <div style={{ marginTop: 4, fontSize: 13 }}>
          <strong>Easiest: drag a folder straight onto this box</strong> — the whole folder uploads with its
          structure preserved. The “Choose a folder” button works too; if a system dialog appears, select the
          folder itself and click <em>Upload</em> (don’t open it and pick files). For RED <code>.RDC</code> clips,
          always add the whole folder so the <code>.R3D</code> parts stay stitched into one clip.
          Large files (50GB+) upload in 100MB parts straight to S3 and resume after Wi-Fi drops.
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>
          <button
            className="cs-btn cs-btn-primary cs-btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              pickFolder();
            }}
            title="Pick a folder (e.g. a RED .RDC) — the whole folder uploads, structure preserved"
          >
            📁 Choose a folder
          </button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>or</span>
          <button
            className="cs-btn cs-btn-ghost cs-btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              inputRef.current?.click();
            }}
          >
            Browse files
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
        />
        {/* Directory attributes are set imperatively in an effect — React
            doesn't reliably pass webkitdirectory/directory through, which would
            silently leave this as a plain (file-only) picker. */}
        <input
          ref={folderRef}
          type="file"
          multiple
          hidden
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
        />
      </div>

      {notice && (
        <div className={`cs-drop-notice ${notice.kind === 'warn' ? 'is-warn' : ''}`}>
          <span>⚠ {notice.text}</span>
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setNotice(null)}>Dismiss</button>
        </div>
      )}

      {!online && (
        <div className="cs-drop-offline">
          You’re offline — uploads paused and will resume from the last finished part when you reconnect.
        </div>
      )}

      {overall.n > 0 && (
        <>
          <div style={{ marginTop: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <strong style={{ fontSize: 14 }}>
                {overall.reconnectingCount > 0
                  ? 'Reconnecting…'
                  : overall.activeCount > 0
                  ? `Uploading ${overall.doneCount}/${overall.n}`
                  : `${overall.doneCount}/${overall.n} uploaded`}
              </strong>
              <span className="cs-time">
                {fmtBytes(overall.loaded)} / {fmtBytes(overall.total)}
                {overall.speed > 0 && overall.reconnectingCount === 0 && ` · ${fmtBytes(overall.speed)}/s · ${fmtEta(overall.eta)} left`}
              </span>
              <span style={{ flex: 1 }} />
              <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={clearDone}>
                Clear finished
              </button>
            </div>
            <div className="cs-progress" style={{ height: 8 }}>
              <div style={{ width: `${Math.round(overall.pct * 100)}%` }} />
            </div>
          </div>

          <UploadStats
            snapshot={snap}
            conn={conn}
            testing={testing}
            onTest={runTest}
            online={online}
            liveBps={overall.speed}
          />
        </>
      )}

      {items.length > 0 && (
        <div style={{ marginTop: 16 }}>
          {items.map((it) => (
            <div className="cs-upload-item" key={it.id}>
              <div style={{ width: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {it.kind === 'photo' ? '🖼' : it.kind === 'rdc' ? '🎞' : '🎬'} {it.name}
              </div>
              <span style={{ color: 'var(--muted)', width: 80 }}>{fmtBytes(it.size)}</span>
              <div className="cs-progress">
                <div style={{ width: `${Math.round((it.progress || 0) * 100)}%` }} />
              </div>
              <span
                style={{
                  width: 130,
                  textAlign: 'right',
                  color: it.status === 'error' ? 'var(--accent)' : 'var(--muted)',
                  fontSize: 12,
                }}
                title={it.error || ''}
              >
                {it.status === 'uploading' && it.reconnecting
                  ? 'reconnecting…'
                  : it.status === 'uploading'
                  ? `${Math.round((it.progress || 0) * 100)}%${it.speed ? ` · ${fmtBytes(it.speed)}/s` : ''}`
                  : it.status}
              </span>
              {it.status === 'uploading' ? (
                <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => cancel(it.id)}>
                  Cancel
                </button>
              ) : it.status === 'error' ? (
                <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => retry(it)}>
                  Retry
                </button>
              ) : (
                <span style={{ width: 64 }} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
